import { confirmEmailToken } from 'api/services/emailConfirmService'
import { getAuthUserSubject } from 'api/utils/AuthManager'
import AuthPageShell from 'components/auth/AuthPageShell'
import PageLoading from 'components/common/loading/PageLoading'
import { useAuth } from 'pages/authenticated/AuthContext'
import { usePermissions } from 'pages/authenticated/PermissionsContext'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { Link, useParams, useSearchParams } from 'react-router-dom'

const ConfirmEmailPage = () => {
  const { t } = useTranslation()
  const { user, isAuthenticated, logout } = useAuth()
  const { refresh } = usePermissions()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const { token: tokenParam } = useParams<{ token?: string }>()
  const token =
    tokenParam?.trim() ||
    searchParams.get('token')?.trim() ||
    searchParams.get('t')?.trim() ||
    ''

  const [status, setStatus] = useState<'loading' | 'success' | 'mismatch' | 'error'>(
    'loading'
  )
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!token) {
        if (!cancelled) setStatus('error')
        return
      }
      const result = await confirmEmailToken(token)
      if (cancelled) return
      if (!result.ok) {
        setStatus('error')
        return
      }

      setConfirmedEmail(result.email)

      const sessionSub = getAuthUserSubject(user)
      const confirmedSub = result.firebaseSub?.trim() || ''
      const sessionEmail =
        typeof user?.email === 'string' ? user.email.trim().toLowerCase() : ''
      const confirmedEmailNorm = result.email?.trim().toLowerCase() || ''

      // Link confirms account B while browser session is still account A.
      if (
        isAuthenticated &&
        confirmedSub &&
        sessionSub &&
        confirmedSub !== sessionSub
      ) {
        setStatus('mismatch')
        return
      }
      if (
        isAuthenticated &&
        confirmedEmailNorm &&
        sessionEmail &&
        confirmedEmailNorm !== sessionEmail
      ) {
        setStatus('mismatch')
        return
      }

      // Refresh role/email flags and listings before the user opens Mon espace.
      if (isAuthenticated) {
        try {
          await refresh()
          await queryClient.invalidateQueries(['myProperties'])
          await queryClient.invalidateQueries(['ownerDashboardSummary'])
        } catch {
          // Navigation still works; Account will refetch on mount.
        }
      }

      setStatus('success')
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [token, isAuthenticated, user, refresh, queryClient])

  if (status === 'loading') {
    return (
      <AuthPageShell
        title={t('auth.emailConfirm.loadingTitle')}
        subtitle={t('auth.emailConfirm.loadingBody')}
      >
        <div className="flex min-h-[8rem] items-center justify-center">
          <PageLoading minHeightClassName="min-h-0" />
        </div>
      </AuthPageShell>
    )
  }

  if (status === 'mismatch') {
    return (
      <AuthPageShell
        title={t('auth.emailConfirm.mismatchTitle')}
        subtitle={t('auth.emailConfirm.mismatchBody')}
      >
        <div className="flex flex-col items-center gap-5 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 text-amber-600">
            <svg
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h18.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
          </div>
          {confirmedEmail ? (
            <p className="text-sm text-gray-600">
              <span className="font-medium text-imovSecondary">{confirmedEmail}</span>
            </p>
          ) : null}
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={() => {
                const q = confirmedEmail
                  ? `?email=${encodeURIComponent(confirmedEmail)}`
                  : ''
                void logout(`/auth${q}`)
              }}
              className="inline-flex min-h-[48px] items-center justify-center rounded-xl bg-imovPrimary px-5 py-3 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark"
            >
              {t('auth.emailConfirm.signInAsConfirmed')}
            </button>
            <Link
              to="/account"
              className="inline-flex min-h-[48px] items-center justify-center rounded-xl border border-gray-200 px-5 py-3 text-sm font-semibold text-imovSecondary transition hover:bg-gray-50"
            >
              {t('auth.emailConfirm.staySignedIn')}
            </Link>
          </div>
        </div>
      </AuthPageShell>
    )
  }

  if (status === 'success') {
    return (
      <AuthPageShell
        title={t('auth.emailConfirm.successTitle')}
        subtitle={t('auth.emailConfirm.successBody')}
      >
        <div className="flex flex-col items-center gap-5 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <svg
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          {confirmedEmail ? (
            <p className="text-sm text-gray-600">
              <span className="font-medium text-imovSecondary">{confirmedEmail}</span>
            </p>
          ) : null}
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
            <Link
              to="/account"
              className="inline-flex min-h-[48px] items-center justify-center rounded-xl bg-imovPrimary px-5 py-3 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark"
            >
              {t('auth.emailConfirm.goToAccount')}
            </Link>
            <Link
              to="/auth"
              state={confirmedEmail ? { email: confirmedEmail } : undefined}
              className="inline-flex min-h-[48px] items-center justify-center rounded-xl border border-gray-200 px-5 py-3 text-sm font-semibold text-imovSecondary transition hover:bg-gray-50"
            >
              {t('auth.emailConfirm.backToAuth')}
            </Link>
          </div>
        </div>
      </AuthPageShell>
    )
  }

  return (
    <AuthPageShell
      title={t('auth.emailConfirm.errorTitle')}
      subtitle={t('auth.emailConfirm.errorBody')}
    >
      <div className="flex flex-col items-center gap-5 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-500">
          <svg
            className="h-7 w-7"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
          <Link
            to="/auth"
            className="inline-flex min-h-[48px] items-center justify-center rounded-xl bg-imovPrimary px-5 py-3 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark"
          >
            {t('auth.emailConfirm.backToAuth')}
          </Link>
          <Link
            to="/account"
            className="inline-flex min-h-[48px] items-center justify-center rounded-xl border border-gray-200 px-5 py-3 text-sm font-semibold text-imovSecondary transition hover:bg-gray-50"
          >
            {t('auth.emailConfirm.goToAccount')}
          </Link>
        </div>
      </div>
    </AuthPageShell>
  )
}

export default ConfirmEmailPage
